"use client";

import { useEffect, useMemo, useState } from "react";
import { ExchangeCurrency } from "@prisma/client";
import useCurrencyStore from "@/app/hooks/useCurrencyStore";
import { formatPrice } from "@/app/utils/formatPrice";

interface LiveaboardListPriceProps {
  /** Price in the trip's native currency. */
  amount: number | null;
  /** Native currency code of the amount (e.g. "IDR", "EUR"). */
  currency: string;
  rates: ExchangeCurrency | null;
}

/**
 * Renders a list price in the navbar-selected currency. The server can't read
 * the currency selector (it lives in localStorage), so SSR falls back to EUR
 * — same as the old static output — and the client swaps in the selection.
 */
export default function LiveaboardListPrice({
  amount,
  currency,
  rates,
}: LiveaboardListPriceProps) {
  const currencyStore = useCurrencyStore();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const displayCode = mounted
    ? currencyStore.selectedCurrency?.code || "EUR"
    : "EUR";

  const { value, code } = useMemo(() => {
    if (amount == null) return { value: null, code: displayCode };
    if (currency === displayCode) return { value: amount, code: currency };
    const from = rates ? (rates as any)[currency] : undefined;
    const to = rates ? (rates as any)[displayCode] : undefined;
    if (typeof from !== "number" || typeof to !== "number" || from <= 0) {
      // Missing rate: show the native price honestly instead of a wrong code.
      return { value: amount, code: currency };
    }
    return { value: (amount / from) * to, code: displayCode };
  }, [amount, currency, displayCode, rates]);

  if (value == null) return <>-</>;
  return (
    <>
      {code} {formatPrice(value)}
    </>
  );
}
